import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { UserAuditLogCategory } from '@shared/services/gql.service';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    @if (status) {
      <div
        class="flex h-8 w-[180px] items-center justify-center rounded border px-2 text-sm"
        [ngClass]="bgClass"
      >
        <div [ngClass]="textClass">{{ status }}</div>
      </div>
    }
  `,
})
export class AuditHistoryCategoryComponent implements ICellRendererAngularComp {
  bgClass!: string;
  params!: ICellRendererParams;
  status!: string;
  textClass!: string;

  // eslint-disable-next-line max-lines-per-function
  agInit(params: ICellRendererParams): void {
    this.params = params;
    const status: string | UserAuditLogCategory = params.value || '';

    if (!status) {
      this.bgClass = '';
      this.textClass = '';
      this.status = '';

      return;
    }

    switch (status) {
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_BUDGET:
        this.bgClass = 'border-aux-gray-dark bg-aux-gray-dark';
        this.textClass = 'text-aux-black';
        this.status = 'Vendor Budgets';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_CHANGE_ORDER:
        this.bgClass = 'border-aux-gray-dark bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Change Order';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_CLOSE_SETTINGS:
        this.bgClass = 'border-aux-gray-dark bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Close Settings';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_CUSTOM_DRIVER:
        this.bgClass = 'border-aux-green-dark bg-white';
        this.textClass = 'text-aux-green-dark';
        this.status = 'Custom Driver';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_DRIVER:
        this.bgClass = 'border-aux-green-dark bg-white';
        this.textClass = 'text-aux-green-dark';
        this.status = 'Drivers';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_EXPENSE_DEFAULTS:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Expense Defaults';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_EXPENSE_SOURCE:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Expense Source';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_FORECAST:
        this.bgClass = 'border-aux-blue bg-white';
        this.textClass = 'text-aux-blue';
        this.status = 'Forecast Settings';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_HISTORICAL_ADJUSTMENT:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Historical Adjustment';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_IN_MONTH_ADJUSTMENTS:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Expense Detail';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_INVESTIGATOR_FORECAST:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Investigator Forecast';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_INVESTIGATOR_SETTINGS:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Investigator Settings';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_INVOICE:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Invoice';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_MANUAL_OVERRIDE:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Manual Override';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PATIENT_DRIVER:
        this.bgClass = 'border-aux-green-dark bg-white';
        this.textClass = 'text-aux-green-dark';
        this.status = 'Patient Driver';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PATIENT_GROUP:
        this.bgClass = 'border-aux-gray-dark bg-aux-gray-dark';
        this.textClass = 'text-aux-black';
        this.status = 'Patient Group';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PATIENT_TRACKER:
        this.bgClass = 'border-aux-gray-dark bg-aux-gray-dark';
        this.textClass = 'text-aux-black';
        this.status = 'Investigator Data';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PAYMENT_MILESTONE:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Payment Milestone';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PREPAIDS:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Prepaids';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PROTOCOL_ENTRY:
        this.bgClass = 'border-aux-gray-dark bg-aux-gray-dark';
        this.textClass = 'text-aux-black';
        this.status = 'Protocol Entry';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_PURCHASE_ORDER:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Purchase Order';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_SITE:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Site';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_SITE_BUDGET:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Site Budget';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_SITE_DRIVER:
        this.bgClass = 'border-aux-green-dark bg-white';
        this.textClass = 'text-aux-green-dark';
        this.status = 'Site Driver';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_SITE_GROUP:
        this.bgClass = 'border-aux-gray-dark bg-aux-gray-dark';
        this.textClass = 'text-aux-black';
        this.status = 'Site Group';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_TIMELINE_EVENT:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Period Close';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_TRIAL_INFO:
        this.bgClass = 'border-aux-gray-dark bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Trial Info';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_TRIAL_TIMELINE:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Trial Timeline';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_TRIAL_TIMELINE_MILESTONE:
        this.bgClass = 'border-aux-warn bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'Timeline Milestone';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_USER:
        this.bgClass = 'border-aux-gray-dark bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'User';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_USER_PERMISSIONS:
        this.bgClass = 'border-aux-gray-dark bg-white';
        this.textClass = 'text-aux-black';
        this.status = 'User Permissions';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_VENDOR:
        this.bgClass = 'border-aux-gray-dark bg-blue-100';
        this.textClass = 'text-aux-black';
        this.status = 'Vendor Contract';
        break;
      case UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_VENDOR_ESTIMATE:
        this.bgClass = 'border-aux-gray-dark bg-aux-gray-dark';
        this.textClass = 'text-aux-black';
        this.status = 'Vendor Estimate';
        break;
      default:
        this.bgClass = '';
        this.textClass = '';
        this.status = Utils.zeroHyphen;
        break;
    }
  }

  refresh(): boolean {
    return false;
  }
}
